import { createSpatial } from './hero-spatial';
import { createChoreography } from './hero-choreography.mjs';

const ARTBOARD_WIDTH = 1440, ARTBOARD_HEIGHT = 940;

export function initHeroScene(root) {
    if (root.heroScene) return root.heroScene;
    const stack = root.querySelector('.hero3d-center-stack');
    const featureEls = [...root.querySelectorAll('.hero3d-feature')];
    const canvas = root.querySelector('.hero3d-canvas');
    const logo = root.querySelector('[data-hero-logo-source]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 575px)');
    let scale = 1, spatial = null, orbitBoost = 1;
    let targetX = 0, targetY = 0;
    let raf = 0, previous = null, clock = 0, visible = true, disposed = false, contextLost = false;
    const choreography = createChoreography({
        stack, featureEls, getScale:() => scale, setOrbitBoost:value => {orbitBoost = value;},
        now:() => clock,
        changed:running => root.dispatchEvent(new CustomEvent(running ? 'hero3d:orbitstart' : 'hero3d:orbitend'))
    });

    function initializeWebGL() {
        try {
            spatial = createSpatial(canvas);
            root.classList.remove('has-webgl-error');
        } catch (error) {
            // HTML/CSS center, six cards and glowballs remain available.
            spatial = null;
            root.classList.add('has-webgl-error');
        }
    }

    function resize() {
        const width = root.clientWidth;
        if (!width) return;
        // Mobile crops only the empty horizontal margins, not the artwork.
        const logicalWidth = mobile.matches ? 1260 : ARTBOARD_WIDTH;
        scale = width / logicalWidth;
        root.style.setProperty('--hero3d-scale', String(scale));
        if (spatial && !contextLost) {
            spatial.resize(scale * ARTBOARD_WIDTH, scale * ARTBOARD_HEIGHT);
            spatial.render(clock/1000,targetX,targetY,orbitBoost,reduced.matches);
        }
    }

    function pointermove(event) {
        if (reduced.matches || event.pointerType === 'touch') return;
        const r = root.getBoundingClientRect();
        const x = Math.max(0,Math.min(1,(event.clientX-r.left)/r.width));
        const y = Math.max(0,Math.min(1,(event.clientY-r.top)/r.height));
        targetX = (x-.5)*2; targetY = (y-.5)*2;
        root.style.setProperty('--hero3d-mx',targetX.toFixed(3));
        root.style.setProperty('--hero3d-my',targetY.toFixed(3));
        root.style.setProperty('--hero3d-gx',(x*100).toFixed(1)+'%');
        root.style.setProperty('--hero3d-gy',(y*100).toFixed(1)+'%');
    }
    function pointerleave() {
        targetX = targetY = 0;
        root.style.setProperty('--hero3d-mx','0'); root.style.setProperty('--hero3d-my','0');
        root.style.setProperty('--hero3d-gx','50%'); root.style.setProperty('--hero3d-gy','50%');
    }

    // One scheduling owner for WebGL, choreography and lifecycle management.
    function tick(now) {
        raf = 0;
        if (disposed || !visible || document.hidden || reduced.matches) {previous = null; return;}
        clock += previous === null ? 0 : now-previous;
        previous = now;
        choreography.update(clock);
        if (spatial && !contextLost) spatial.render(clock/1000,targetX,targetY,orbitBoost,false);
        raf = window.requestAnimationFrame(tick);
    }
    function stop() { window.cancelAnimationFrame(raf); raf = 0; previous = null; }
    function wake() {
        const paused = !visible || document.hidden;
        root.classList.toggle('is-paused',paused);
        if (!disposed && !paused && !reduced.matches && !raf) raf = window.requestAnimationFrame(tick);
    }
    function runSignatureOrbit() {
        if (disposed || reduced.matches) return false;
        const started = choreography.runSignatureOrbit();
        if (started) wake();
        return started;
    }
    function keydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {event.preventDefault();runSignatureOrbit();}
    }
    function visibility() {if (document.hidden) stop();wake();}
    function motion() {
        stop(); choreography.cancel(); pointerleave();
        if (spatial && !contextLost) spatial.render(clock/1000,0,0,1,true);
        wake();
    }
    function lost(event) {event.preventDefault();contextLost = true;root.classList.add('has-webgl-error');}
    function restored() {contextLost = false;root.classList.remove('has-webgl-error');resize();wake();}
    function logoError() {root.classList.add('has-logo-error');}
    const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (!visible) stop();
        wake();
    });
    const resizeObserver = new ResizeObserver(resize);

    function destroy() {
        disposed = true;stop();choreography.cancel();
        observer.disconnect();resizeObserver.disconnect();
        root.removeEventListener('pointermove',pointermove);root.removeEventListener('pointerleave',pointerleave);
        stack.removeEventListener('click',runSignatureOrbit);stack.removeEventListener('keydown',keydown);
        document.removeEventListener('visibilitychange',visibility);reduced.removeEventListener('change',motion);
        mobile.removeEventListener('change',resize);
        canvas.removeEventListener('webglcontextlost',lost);canvas.removeEventListener('webglcontextrestored',restored);
        logo.removeEventListener('error',logoError);
        window.removeEventListener('pagehide',pagehide);window.removeEventListener('pageshow',pageshow);
        if (spatial) spatial.dispose();
        root.classList.remove('is-ready');delete root.heroScene;
    }
    function pagehide(event) {if (event.persisted) {stop();root.classList.add('is-paused');} else destroy();}
    function pageshow() {resize();wake();}

    initializeWebGL();resize();
    root.addEventListener('pointermove',pointermove);root.addEventListener('pointerleave',pointerleave);
    stack.addEventListener('click',runSignatureOrbit);stack.addEventListener('keydown',keydown);
    document.addEventListener('visibilitychange',visibility);reduced.addEventListener('change',motion);
    mobile.addEventListener('change',resize);
    canvas.addEventListener('webglcontextlost',lost);canvas.addEventListener('webglcontextrestored',restored);
    logo.addEventListener('error',logoError);
    window.addEventListener('pagehide',pagehide);window.addEventListener('pageshow',pageshow);
    observer.observe(root);resizeObserver.observe(root);
    root.heroScene = {runSignatureOrbit, triggerOrbit:runSignatureOrbit, destroy};
    root.classList.add('is-ready');wake();
    return root.heroScene;
}

window.IdbarahHeroScene = {init:initHeroScene, triggerOrbit:root => initHeroScene(root).runSignatureOrbit()};
function boot() {document.querySelectorAll('[data-hero-scene]').forEach(initHeroScene);}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true}); else boot();
